import { h } from '@stencil/core';
import { ActionButtonDto, ButtonType, HrefButtonDto, PromptButtonDto } from 'client/types';
import { triggerOpenNewLink, triggerSubmitChatMessage, triggerTrackEvent } from 'utils/events';
import { TrackEventWithoutPageview } from 'utils/tracker';
import EvaButton from './EvaButton';

interface GenericButtonProps {
  button: HrefButtonDto | PromptButtonDto | ActionButtonDto;
  doBefore?: () => void;
  doAfter?: () => void;
  disabled?: boolean;
  rootElementClasses?: string;
}

const handleClickWithTracking = (event: Event, trackData: TrackEventWithoutPageview, action: () => void, before: () => void = () => {}, after: () => void = () => {}) => {
  before();
  event.stopPropagation();
  triggerTrackEvent(trackData);
  action();
  after();
};

// Type guard for ActionButtonDto
const isActionButton = (button: any): button is ActionButtonDto => button.type === ButtonType.ACTION;

// Type guard for PromptButtonDto
const isPromptButton = (button: any): button is PromptButtonDto => button.type === ButtonType.PROMPT;

// Type guard for HrefButtonDto
const isHrefButton = (button: any): button is HrefButtonDto => button.type === ButtonType.HREF;

// Get the appropriate click handler dynamically based on the button type
const getClickHandler = (button: HrefButtonDto | PromptButtonDto | ActionButtonDto, doBefore?: () => void, doAfter?: () => void) => {
  return (event: Event) => {
    if (isActionButton(button)) {
      handleClickWithTracking(
        event,
        { event_type: 'click', event_name: 'click_action_button', data: { label: button.label, actionId: button.action.actionId, params: button.action.params } },
        () =>
          triggerSubmitChatMessage({
            message: button.userMessage,
            type: 'USER',
            from: 'FILTER',
            method: 'TEXT',
            action: button.action,
          }),
        doBefore,
        doAfter,
      );
    } else if (isPromptButton(button)) {
      handleClickWithTracking(
        event,
        { event_type: 'click', event_name: 'click_prompt_button', data: { prompt: button.prompt } },
        () =>
          triggerSubmitChatMessage({
            message: button.prompt,
            type: 'USER',
            from: 'FILTER',
            method: 'TEXT',
          }),
        doBefore,
        doAfter,
      );
    } else if (isHrefButton(button)) {
      handleClickWithTracking(
        event,
        { event_type: 'click', event_name: 'click_link_button', data: { link: button.link } },
        () => triggerOpenNewLink({ newTab: true, href: button.link }),
        doBefore,
        doAfter,
      );
    } else {
      console.warn('Unknown button type:', button);
      event.stopPropagation();
    }
  };
};

// Main GenericButton Component
const GenericButton = ({ button, doBefore, doAfter, disabled = false, rootElementClasses }: GenericButtonProps) => {
  const clickHandler = getClickHandler(button, doBefore, doAfter);

  return (
    <EvaButton
      icon={button.icon}
      text={button.label}
      size={button.size || 'md'}
      variant={button.variant || '-primary'}
      rootElementClasses={rootElementClasses}
      disabled={disabled}
      onClick={clickHandler}
    />
  );
};

export default GenericButton;
